// @flow
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { get, isEqual } from 'lodash';
import { logout } from '@context/user';
import {
  fetchGoogleCalendarStatus, disconnectGoogleCalendar, offersGoogleCalendar, trackGoogleCalendarEvent,
} from '@context/googleCalendar';
import { mapBundledPermission } from '@helpers/connect';
import { UserNotifications } from '@helpers/helpers';
import GoogleCalendarView from '../components/views/Settings/GoogleCalendar/GoogleCalendar';
import useGoogleCalendarConnect from '../components/views/Settings/GoogleCalendar/useGoogleCalendarConnect';

// Ajustes › Google Agenda, for doctors. Connecting happens in the browser (the system browser in the
// apps): the status is loaded again whenever the user comes back, so it says "Conectado" on return.
const GoogleCalendar = () => {
  const dispatch = useDispatch();
  const user = useSelector(state => get(state, ['context', 'user']));
  const permissions = useSelector(state => mapBundledPermission(state), isEqual);
  const status = useSelector(state => get(state, ['context', 'googleCalendar'], null));
  const isDoctor = get(user, 'roleType') === 'Doctor';

  const { opened, connect: handleConnect } = useGoogleCalendarConnect();
  // The status loaded since the screen opened: a stored one saying it isn't offered may be old
  const [checked, setChecked] = useState(false);

  // On opening, and on every return: the app back to the foreground (Cordova's resume), the tab visible
  // again, the window focused again. They fire together on one return; the request is shared.
  useEffect(() => {
    if (!isDoctor) return undefined;

    const refresh = () => dispatch(fetchGoogleCalendarStatus());
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    let active = true;
    refresh().then(() => { if (active) setChecked(true); });

    document.addEventListener('resume', refresh);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', refresh);

    return () => {
      active = false;
      document.removeEventListener('resume', refresh);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', refresh);
    };
  }, [dispatch, isDoctor]);

  const handleDisconnect = useCallback(() => {
    UserNotifications.confirmI18n('alert/google_calendar/disconnect', 'warning', '#FFA433', true)
      .then((confirmed) => {
        if (confirmed) dispatch(disconnectGoogleCalendar());
      });
  }, [dispatch]);

  const handleReload = useCallback(() => dispatch(fetchGoogleCalendarStatus()), [dispatch]);

  const handleLogout = useCallback(() => dispatch(logout()), [dispatch]);

  const handleUpgrade = useCallback(() => dispatch(trackGoogleCalendarEvent('upgrade_clicked')), [dispatch]);

  // Not for secretaries, nor when the API can't connect Google accounts
  const offered = offersGoogleCalendar(status, isDoctor);
  const loaded = Boolean(get(status, 'lastFetch'));
  const settled = checked && !get(status, 'isLoading');

  // The paywall seen, once per visit, on a status loaded since the screen opened
  const paywallCounted = useRef(false);
  const showsPaywall = checked && offered && get(status, 'upgradeRequired') === true;
  useEffect(() => {
    if (!showsPaywall || paywallCounted.current) return;
    paywallCounted.current = true;
    dispatch(trackGoogleCalendarEvent('paywall_viewed'));
  }, [dispatch, showsPaywall]);

  if (!isDoctor || (settled && loaded && !offered)) return <Redirect to="/ajustes" />;

  return (
    <GoogleCalendarView
      user={user}
      permissions={permissions}
      onLogout={handleLogout}
      // A stored status saying it isn't offered: a spinner until the new one comes
      status={offered || !loaded ? status : null}
      opened={opened}
      onConnect={handleConnect}
      onDisconnect={handleDisconnect}
      onReload={handleReload}
      onUpgrade={handleUpgrade}
    />
  );
};

export default GoogleCalendar;
